import { getStationTier } from '../utils/stationTier';

// API response structure (new format with geoPoint)
export interface WeatherStationAPIResponse {
  stationCode: string;
  geoPoint: {
    longitude: number;
    latitude: number;
  };
  elevation: number;
  tags: {
    [key: string]: string;
  };
  firstObsTimestamp: string;
  lastObsTimestamp?: string;
  startTime?: string; // ISO 8601 timestamp when station was first added to the system
  devices: Array<{
    stationId: string;
    sourceStationId?: string;
    dataSourceId: string;
    type: string;
  }>;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  /** Honest GADM unit types + stable GID codes (present on the details/locations
   *  projection; the ObsWrapper LIST projection currently omits them). */
  stateType?: string;
  cityType?: string;
  districtType?: string;
  countryCode?: string;
  stateCode?: string;
  cityCode?: string;
  districtCode?: string;
  admin?: AdminUnit[] | string;
  active?: boolean;
  parameters?: string[];
}

/** One GADM administrative level for a station: real name + unit type + GID. */
export interface AdminUnit {
  level: number; // 0=country, 1=admin1 (state/province/…), 2=admin2 (county/…), 3=admin3 (district/ward/barangay)
  name: string;
  type: string; // GADM ENGTYPE, e.g. "Country", "State", "County", "District", "Municipality"
  gid?: string;
}

// Internal application structure (flattened for easier use)
export interface WeatherStation {
  stationCode: string;
  tags: {
    mmi?: string;
    mgID?: string;
    name?: string;
    city?: string;
    [key: string]: string | undefined;
  };
  latitude: number;
  longitude: number;
  elevation: number;
  firstObsTimestamp: string;
  lastObsTimestamp?: string;
  startTime?: string; // ISO 8601 timestamp when station was first added to the system
  devices: Array<{
    stationId: string;
    sourceStationId?: string;
    dataSourceId: string;
    type: string;
    /** Whether this device/network is currently active. false = offline (kept, but labeled). */
    active?: boolean;
  }>;
  country?: string;
  state?: string;
  /** GADM admin2 (NAME_2) — a county/district/etc., NOT necessarily a city. */
  city?: string;
  /** GADM admin3 (NAME_3) — a district/ward/barangay/etc. (deepest tier). */
  district?: string;
  /** GADM unit type for `state`/admin1 (e.g. "State", "Province", "Region"). */
  stateType?: string;
  /** GADM unit type for `city`/admin2 (e.g. "County", "District") so it's labeled honestly. */
  cityType?: string;
  /** GADM unit type for `district`/admin3 (e.g. "Barangay", "Ward"). */
  districtType?: string;
  /** GADM GID codes per level (stable identifiers). */
  countryCode?: string;
  stateCode?: string;
  cityCode?: string;
  districtCode?: string;
  /** Structured GADM admin hierarchy; flat country/state/city/district are derived aliases. */
  admin?: AdminUnit[];
  distance?: number; // Distance from search center in km (for coordinate searches)
  isActive?: boolean; // Whether the station is currently active/online (has recent observations)
  isPrimaryResult?: boolean; // Whether this is the primary search result (vs. nearby context station)
  parameters?: string[]; // Available observation parameters for this station
  stationTier?: 'public' | 'plus' | 'internal'; // Data source tier for marker coloring
  /**
   * Per-parameter temporal coverage: parameter → [firstYM, lastYM] where
   * ym = year*12 + (month-1). Precomputed offline (scripts/build-coverage-index.js).
   * NOT loaded with allStations (too large) — fetched lazily per station via
   * useStationCoverage (GET /api/station-metadata/:code/coverage). Kept on the
   * type as the canonical shape; populated only where a coverage fetch merged it in.
   */
  coverage?: Record<string, [number, number]>;
}

/**
 * Normalize a station's `admin` field (array or JSON string) into AdminUnit[].
 * Mirrors the tolerant parsing in stationDataStore so the search-results path
 * and the map/all-stations path agree on shape. Returns undefined when absent
 * or malformed.
 */
export function parseAdmin(raw: AdminUnit[] | string | undefined): AdminUnit[] | undefined {
  const arr = typeof raw === 'string'
    ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
    : raw;
  if (!Array.isArray(arr)) return undefined;
  return (arr as Array<Record<string, unknown>>)
    .filter((a) => a && typeof a.level === 'number' && !!a.name)
    .map((a) => ({
      level: a.level as number,
      name: String(a.name),
      type: String(a.type ?? ''),
      gid: a.gid ? String(a.gid) : undefined,
    }));
}

// Transform API response to internal format
export function transformAPIResponse(apiStation: WeatherStationAPIResponse): WeatherStation {
  return {
    stationCode: apiStation.stationCode,
    latitude: apiStation.geoPoint?.latitude ?? 0,
    longitude: apiStation.geoPoint?.longitude ?? 0,
    elevation: apiStation.elevation,
    tags: apiStation.tags || {},
    firstObsTimestamp: apiStation.firstObsTimestamp,
    lastObsTimestamp: apiStation.lastObsTimestamp,
    startTime: apiStation.startTime,
    devices: apiStation.devices || [],
    country: apiStation.country,
    state: apiStation.state,
    city: apiStation.city,
    district: apiStation.district,
    // Honest GADM types + codes + structured hierarchy. The ObsWrapper LIST
    // projection currently omits these (empty), so they're a no-op there; carried
    // through so the search-results path renders honest types once upstream (or a
    // /locations join) supplies them — matching the map/all-stations path.
    stateType: apiStation.stateType,
    cityType: apiStation.cityType,
    districtType: apiStation.districtType,
    countryCode: apiStation.countryCode,
    stateCode: apiStation.stateCode,
    cityCode: apiStation.cityCode,
    districtCode: apiStation.districtCode,
    admin: parseAdmin(apiStation.admin),
    // Use the 'active' field from API if available, otherwise will be set by markStationsActiveStatus
    isActive: apiStation.active,
    parameters: (apiStation as unknown as { parameters?: string[] }).parameters,
    stationTier: getStationTier(apiStation.devices || []),
  };
}
